import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, buildArray, parseDate, isNotNilOrEmpty} from 'shared/web';

import {getLevelIndicatorFlags} from '../utils/getLevelIndicatorFlags';
import {LevelIndicator} from '../utils/levelIndicatorsFor';

interface HeaderData {
  serviceCaseNumber: string | Nullish;
  publicId: string | Nullish;
  vin: string | Nullish;
  registrationPlate: string | Nullish;
  firstRegistrationOn: string | Nullish;
  warrantyDate: string | Nullish;
  isVehicleDetail: boolean | Nullish;
  isNotInService?: boolean | Nullish;
  mileage?: string | Nullish;
  fuelTank?: string | Nullish;
  /**
   * Rendered instead of the `fuelTank` title when set (for example the compact
   * `FuelLevelValue` gauge) — `ParametersItem` drops the title entirely once `content`
   * is present. The tooltip stays the i18n label and the gauge brings its own
   * `aria-label`, so the `fuelTank` string itself is no longer read out.
   */
  fuelTankContent?: ReactNode;
  batteryCharge?: string | Nullish;
  /** Battery counterpart of `fuelTankContent`, on the same terms. */
  batteryChargeContent?: ReactNode;
  /**
   * Which level gauges the vehicle's fuel type calls for, from `levelIndicatorsFor` — an
   * electric vehicle has no fuel tank to show. Omit it to keep the fuel tank alone.
   */
  levelIndicators?: LevelIndicator[];
  fuelType?: string | Nullish;
  engineCode?: string | Nullish;
  technicalInspectionValidUntil?: string | Nullish;
  hasComments?: boolean;
  additionalNote?: string | Nullish;
  onIconClick?: () => void;
}

export function useGetVehicleHeaderV2(data: HeaderData): Parameter[] {
  const formatDateTime = useDateTimeFormatter();
  const formatDate = (dateValue: string) => formatDateTime('dateShort', parseDate(dateValue));
  const {hasFuel, hasBattery} = getLevelIndicatorFlags(data.levelIndicators ?? ['fuel']);

  return buildArray<Parameter>()
    .when(isNotNilOrEmpty(data.additionalNote), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: data.additionalNote,
      onClick: data.onIconClick,
    })
    .when(data.hasComments, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: data.onIconClick,
    })
    .when(data.isVehicleDetail, {
      title: data?.publicId || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.publicId'),
    })
    .whenNot(data.isVehicleDetail, {
      title: data?.serviceCaseNumber || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.serviceCase.labels.number'),
    })
    .add({
      title: data?.registrationPlate || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
    })
    .add({
      title: data?.vin || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.vin'),
    })
    .add({
      title: data?.firstRegistrationOn ? formatDate(data?.firstRegistrationOn) : EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.firstRegistration'),
    })
    .add({
      title: data?.technicalInspectionValidUntil
        ? formatDate(data?.technicalInspectionValidUntil)
        : EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.workshop.labels.technicalInspectionValidUntil'),
    })
    .add({
      title: data?.warrantyDate ? formatDate(data?.warrantyDate) : EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.warrantyUntil'),
    })
    .add({
      title: data?.fuelType || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.fuel'),
    })
    .add({
      title: data?.engineCode || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.engineCode'),
    })
    .when(data.isNotInService, {
      title: i18n.t('entity.vehicle.labels.isNotInService'),
      tooltip: i18n.t('entity.vehicle.labels.isNotInServiceTooltip'),
    })
    .whenNot(data.isVehicleDetail, {
      title: data.mileage || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.actualMileage'),
    })
    .when(!data.isVehicleDetail && hasFuel, {
      title: data.fuelTank || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.workshop.labels.fuelTank'),
      // Stroke-drawn glyph - keep isIconNotFilled, or the fill rule floods the outline.
      icon: isNotNil(data.fuelTankContent) ? 'specification/fuel_pump_thin' : undefined,
      isIconNotFilled: isNotNil(data.fuelTankContent) ? true : undefined,
      variant: isNotNil(data.fuelTankContent) ? 'secondary' : undefined,
      content: data.fuelTankContent,
    })
    .when(!data.isVehicleDetail && hasBattery, {
      title: data.batteryCharge || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.batteryCharge'),
      // Filled glyph - no isIconNotFilled, or its hardcoded colour overrides the variant.
      icon: isNotNil(data.batteryChargeContent) ? 'electricalParams/charging' : undefined,
      variant: isNotNil(data.batteryChargeContent) ? 'secondary' : undefined,
      content: data.batteryChargeContent,
    });
}
